// https://github.paypal.com/pages/Tracking-R/client-side-instrumentation/#record_ac

import { isRenderingOnServer } from '../helper'
import poll from '../poll'
import { fptiTypes } from './fpti-constants'

// Validate if analytics object is available on window.
const validateAnalyticsObject = () => {
  if (
    !(typeof window !== 'undefined' && window.PAYPAL && window.PAYPAL.analytics)
  ) {
    return Promise.reject()
  }
  return Promise.resolve()
}

/**
 *  Function to poll and check if analytics is available on window. If available fire FPTI.
 * @param options
 * @param callback
 * @returns {Promise<never>}
 */
export const trackingHelper = (options, callback) =>
  poll(validateAnalyticsObject, 4, 500)
    .then(() => {
      callback(options)
    })
    .catch(() => {})

const generateFptiTagValues = ({ name, type, event_name }, pageName) => {
  if (!name || !type || !pageName) {
    throw new Error('Missing name || type || pageName to generate FPTI data')
  }

  let fpti = {}
  switch (type) {
    case fptiTypes.SUCCESS: {
      fpti.page = `main:help:center:${name}`
      fpti.event_name = event_name
        ? event_name
        : `${name.replaceAll('-', '_')}_shown_in_${pageName}_page`
      break
    }

    case fptiTypes.ERROR: {
      fpti.page = `main:help:center:${name}-error`
      fpti.event_name = event_name
        ? event_name
        : `${name.replaceAll('-', '_')}_error_in_${pageName}_page`
      break
    }

    case fptiTypes.ON_CLICK: {
      fpti.page = `main:help:center:${name}`
      fpti.event_name = event_name
        ? event_name
        : `${name.replaceAll('-', '_')}_click_in_${pageName}_page`
      break
    }

    case fptiTypes.NO_DATA: {
      fpti.page = `main:help:center:${name}-no-data`
      fpti.event_name = event_name
        ? event_name
        : `${name.replaceAll('-', '_')}_no_data_to_render_in_${pageName}_page`
      break
    }

    case fptiTypes.VALIDATION_ERROR: {
      fpti.page = `main:help:center:${name}-validation-error`
      fpti.event_name = event_name
        ? event_name
        : `${name.replaceAll('-', '_')}_validation_error_in_${pageName}_page`
      break
    }

    case fptiTypes.SERVICE_ERROR: {
      fpti.page = `main:help:center:${name}-service-error`
      fpti.event_name = event_name
        ? event_name
        : `${name.replaceAll('-', '_')}_service_error_in_${pageName}_page`
      break
    }

    default: {
      fpti.event_name = ''
      break
    }
  }

  return fpti
}

// Get FPTI Options
// eslint-disable-next-line complexity
export const mapFptiOptions = (fptiOptions, baseOptions) => {
  if (isRenderingOnServer()) {
    return {}
  }

  const FPTIObject = (window && window.fpti) || {}

  // generate FPTI tags and values

  const generatedOptions = generateFptiTagValues(
    fptiOptions,
    baseOptions.pageName,
  )

  // clearing out error props in case they exist
  FPTIObject.erpg = null
  FPTIObject.erfd = null
  FPTIObject.eccd = null

  // For setting link
  if (fptiOptions.link) {
    FPTIObject.link = fptiOptions.link || ''
    FPTIObject.pgln = `${generatedOptions.page}|${fptiOptions.link}`
    FPTIObject.pglk = `${baseOptions.pgrp}|${fptiOptions.link}`
  }

  // Set event Type
  FPTIObject.e = fptiOptions.e || null

  // set article doc id
  FPTIObject.article_doc_id = fptiOptions.article_doc_id || null

  // add hpid if it is part of the options, else clear it
  FPTIObject.hpid = fptiOptions.hpid || null

  // add help_doc_id and help_component_ids if available in the options, else clear them
  FPTIObject.help_doc_id = fptiOptions.help_doc_id || ''
  FPTIObject.help_component_ids = fptiOptions.help_component_ids || ''

  // Search keyword
  FPTIObject.srch = fptiOptions.srch || null // TODO:Datamasking - check if data masking is required as we are doing in smarthelp

  // customer confirmed
  FPTIObject.link_name = fptiOptions.link_name || ''

  FPTIObject.channel_name = fptiOptions.channel_name || ''

  FPTIObject.rpa = fptiOptions.rpa || null

  FPTIObject.hcar = fptiOptions.hcar || null

  FPTIObject.hcaf = fptiOptions.hcaf || null

  FPTIObject.exp = fptiOptions.exp || null

  return { ...FPTIObject, ...baseOptions, ...generatedOptions }
}

// // Track Impression event
export const trackImpression = (params) =>
  trackingHelper(params, (options) => {
    const fptiData = mapFptiOptions(options)
    return window.PAYPAL.analytics.Analytics.prototype.recordImpression({
      data: fptiData,
    })
  })

export const trackActivityEvent = (params) =>
  trackingHelper(params, (options) => {
    const fptiData = options
    fptiData.e = 'ac'
    window.PAYPAL.analytics.Analytics.prototype.recordEvent(
      fptiData.event_name,
      { data: fptiData },
    )
  })

// Track Click Event
export const trackClickEvent = (params) =>
  trackingHelper(params, (options) => {
    const fptiData = options
    fptiData.e = 'cl'
    window.PAYPAL.analytics.Analytics.prototype.recordEvent(
      fptiData.event_name,
      { data: fptiData },
    )
  })

// // Track Javascript Error
// https://github.paypal.com/pages/Tracking-R/client-side-instrumentation/#record_error
export const trackJSError = (errorObject) => {
  if (window.PAYPAL && window.PAYPAL.trackJSError) {
    window.PAYPAL.trackJSError(errorObject)
  }
  // PAYPAL.analytics.logJSError(errorObject, ['TopicTree', ['errorType']]);
}
